import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { RestaurantsService } from "../../services/Restaurants";
import { MAX_IMAGE_SIZE_BYTES, MAX_IMAGE_SIZE_MB } from "../../utils/imageUploadLimit";
import Dropdown from "./dropdown";
import {
  getStyleProfile,
  getAbvCategory,
  getIbuCategory,
  getGroupBadgeColor,
  getLevelBadgeColor,
  getOriginClassification,
  isImportedFromCountry,
  CORE_STYLES,
} from "../../utils/beerTaxonomy";

const INPUT_CLASS =
  "w-full p-2.5 bg-[#fef5d4] text-gray-800 border border-[#eed98b] rounded focus:outline-none text-left";
const inputClassName = (hasError) =>
  `w-full p-2.5 bg-[#fef5d4] text-gray-800 border ${hasError ? "border-red-500" : "border-[#eed98b]"} rounded focus:outline-none text-left`;
const LABEL_CLASS = "block text-sm font-medium text-gray-700 mb-1 text-left";
const ROW_LABEL_CLASS = "block text-xs font-medium text-gray-600 mb-1 text-left";

const FORMAT_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "can", label: "Can" },
  { value: "bottle", label: "Bottle" },
];

function Section({ number, title, subtitle, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-left">
      <div className="mb-4 text-left">
        <h3 className="text-sm font-bold text-gray-800 text-left">
          {number}. {title}
        </h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5 text-left">{subtitle}</p>}
      </div>
      <div className="space-y-4 text-left">{children}</div>
    </div>
  );
}

function Badge({ className = "", children }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${className}`}>
      {children}
    </span>
  );
}

function Checkbox({ name, label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700 text-left">
      <input
        type="checkbox"
        name={name}
        checked={!!checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-gray-300 text-[#d72134] focus:ring-[#d72134] flex-shrink-0"
      />
      <span className="text-left">{label}</span>
    </label>
  );
}

export default function BeerItemEditPanel({
  item,
  onClose,
  onSave,
  isSaving,
  validBeerTypes = CORE_STYLES,
  restaurantScope,
  restaurantScopeName,
}) {
  const isEditMode = !!item;
  const scopedRestaurantUuid = !isEditMode && restaurantScope && restaurantScope !== "all" ? restaurantScope : null;

  const [formData, setFormData] = useState({
    restaurant_uuid: "",
    brewery_name: "",
    product_name: "",
    style: "",
    abv: "",
    ibu: "",
    origin: {
      city: "",
      state: "",
      country: "",
    },
    is_imported: null,
    is_filtered: false,
    is_non_alcoholic: false,
    has_gluten: true,
    has_lactose: false,
    has_nuts: false,
    is_vegan: false,

    body: "",
    texture: "",
    intensity: "",

    description: "",
    seasonal: false,
    limited: false,
    image_url: "",
    status: true,
    notes: "",
  });

  const categories = validBeerTypes;

  const [error, setError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [restaurants, setRestaurants] = useState([]);

  const rowIdCounter = useRef(0);
  const createRow = (overrides = {}) => ({
    id: `row-${rowIdCounter.current++}`,
    format: "draft",
    serving_size: "",
    price: "",
    ...overrides,
  });
  const [servingRows, setServingRows] = useState(() => [createRow()]);

  useEffect(() => {
    if (item) {
      let rows = [];
      if (Array.isArray(item.formats)) {
        item.formats.forEach((f) => {
          const sizes = Array.isArray(f.serving_sizes) ? f.serving_sizes : [];
          const prices = Array.isArray(f.prices) ? f.prices : [];
          const len = Math.max(sizes.length, prices.length);
          for (let i = 0; i < len; i++) {
            rows.push(
              createRow({
                format: f.format || "draft",
                serving_size: sizes[i] ?? "",
                price: prices[i] !== undefined && prices[i] !== null ? String(prices[i]) : "",
              })
            );
          }
        });
      }
      setServingRows(rows.length > 0 ? rows : [createRow()]);

      setFormData({
        ...item,
        origin: item.origin || { country: "", state: "", city: "" },
        is_filtered: item.is_filtered === true,
        is_non_alcoholic: item.is_non_alcoholic || false,
        has_gluten: item.has_gluten !== false,
        has_lactose: item.has_lactose || false,
        has_nuts: item.has_nuts || false,
        is_vegan: item.is_vegan || false,
        body: item.body || "",
        texture: item.texture || "",
        intensity: item.intensity || "",
        seasonal: item.seasonal || false,
        limited: item.limited || false,
        status: item.status !== false,
        image_url: item.image_url || "",
      });

      const baseUrl = process.env.REACT_APP_IMAGE_BASE_URL || "";
      let previewUrl = "";
      if (item.image_url && !item.image_url.includes("default_image_beer")) {
        if (item.image_url.startsWith("http://") || item.image_url.startsWith("https://")) {
            previewUrl = item.image_url;
        } else {
            previewUrl = `${baseUrl}${item.image_url.startsWith('/') ? '' : '/'}${item.image_url}`;
        }
      }
      setImagePreview(previewUrl);
    }
  }, [item]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const data = await RestaurantsService.getAllRestaurants("name");
        setRestaurants(data?.restaurants || data || []);
      } catch (err) {}
    };
    fetchRestaurants();
  }, []);

  // Inherit the scoped restaurant automatically when adding from a single-restaurant Manage Menu view
  useEffect(() => {
    if (scopedRestaurantUuid) {
      setFormData((prev) =>
        prev.restaurant_uuid === scopedRestaurantUuid ? prev : { ...prev, restaurant_uuid: scopedRestaurantUuid }
      );
    }
  }, [scopedRestaurantUuid]);

  // Derive Body/Texture/Flavor Intensity from the selected Beer Style (read-only Style Profile)
  useEffect(() => {
    const profile = getStyleProfile(formData.style);
    const body = profile?.body || "";
    const texture = profile?.texture || "";
    const intensity = profile?.flavorIntensity || "";
    setFormData((prev) => {
      if (prev.body === body && prev.texture === texture && prev.intensity === intensity) return prev;
      return { ...prev, body, texture, intensity };
    });
  }, [formData.style]);

  // Auto-classify Domestic vs Imported from Country of Origin
  useEffect(() => {
    const imported = isImportedFromCountry(formData.origin?.country);
    setFormData((prev) => (prev.is_imported === imported ? prev : { ...prev, is_imported: imported }));
  }, [formData.origin?.country]);

  const styleProfile = getStyleProfile(formData.style);
  const abvCategory = getAbvCategory(formData.abv);
  const ibuCategory = getIbuCategory(formData.ibu);
  const originClassification = getOriginClassification(formData.origin?.country);

  const validateForm = () => {
    const errors = {};
    if (!isEditMode && !scopedRestaurantUuid && !formData.restaurant_uuid) errors.restaurant_uuid = "Restaurant is required";
    if (!formData.brewery_name?.trim()) errors.brewery_name = "Brewery name is required";
    if (!formData.product_name?.trim()) errors.product_name = "Product name is required";
    if (!formData.style?.trim()) errors.style = "Beer style is required";
    if (!formData.origin?.country?.trim()) errors.country = "Country is required";

    if (formData.abv === "" || formData.abv === null || formData.abv === undefined) {
      errors.abv = "ABV is required";
    } else {
      const abvNum = Number(formData.abv);
      if (isNaN(abvNum) || abvNum < 0 || abvNum > 25) errors.abv = "ABV must be between 0 and 25";
    }

    const hasIncompleteRow = servingRows.some((row) => {
      const hasSize = row.serving_size.trim() !== "";
      const hasPrice = row.price !== "";
      if (hasSize !== hasPrice) return true;
      if (hasPrice && isNaN(Number(row.price))) return true;
      return false;
    });
    if (hasIncompleteRow) errors.servingRows = "Each serving row needs both a size and a valid price.";

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("origin.")) {
      const field = name.split(".")[1];
      setFormData(prev => ({
        ...prev,
        origin: { ...prev.origin, [field]: value },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (validationErrors[name] || (name.startsWith("origin.") && validationErrors.country)) {
      setValidationErrors((prev) => ({ ...prev, [name]: undefined, country: undefined }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleStyleSelect = (option) => {
    setFormData((prev) => ({ ...prev, style: option.value }));
    if (validationErrors.style) setValidationErrors((prev) => ({ ...prev, style: undefined }));
  };

  const handleServingRowChange = (id, field, value) => {
    setServingRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    if (validationErrors.servingRows) setValidationErrors((prev) => ({ ...prev, servingRows: undefined }));
  };

  const handleAddServingRow = () => setServingRows((prev) => [...prev, createRow()]);
  const handleRemoveServingRow = (id) => setServingRows((prev) => prev.filter((row) => row.id !== id));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Same types the backend's image upload accepts.
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError("Please select a valid image file (JPG, PNG, or WebP)");
        return;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        setError(`Image size should be less than ${MAX_IMAGE_SIZE_MB}MB`);
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setError(null);

      const formatsMap = {};
      servingRows.forEach((row) => {
        const size = row.serving_size.trim();
        if (!size || row.price === "") return;
        const priceNum = Number(row.price);
        if (isNaN(priceNum)) return;
        if (!formatsMap[row.format]) {
          formatsMap[row.format] = { format: row.format, serving_sizes: [], prices: [] };
        }
        formatsMap[row.format].serving_sizes.push(size);
        formatsMap[row.format].prices.push(priceNum);
      });

      const { archived_at, ...editableFormData } = formData;
      const submitData = {
        ...editableFormData,
       
        is_non_alcoholic: abvCategory === "Non-Alcoholic",
        formats: Object.values(formatsMap),
      };

      onSave(submitData, imageFile);
    } catch (err) {
      setError(err.message || "Failed to save beer");
    }
  };

  const inheritedRestaurantName = isEditMode
    ? item?.restaurantname || restaurants.find((r) => r.uuid === formData.restaurant_uuid)?.name
    : restaurantScopeName;

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[60%] lg:w-[55%] bg-white border-l border-gray-200 shadow-lg z-50 overflow-y-auto animate-slide-in-right">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
        <div className="text-left">
          <h2 className="text-xl font-bold font-['Outfit'] text-left">
            {item ? "Edit Beer Item" : "Add Beer Item"}
          </h2>
          {(scopedRestaurantUuid || isEditMode) && inheritedRestaurantName && (
            <p className="text-xs text-gray-500 mt-0.5 text-left">Restaurant: {inheritedRestaurantName}</p>
          )}
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 bg-gray-100 p-2 rounded-full">
          <X className="h-5 w-5" />
        </button>
      </div>

      {error && <div className="p-4 m-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

      <form onSubmit={handleSubmit} className="p-6 space-y-6">

        {/* 1. Beer Image */}
        <Section number={1} title="Beer Image" subtitle={`Upload or replace the product image. PNG or JPG, maximum ${MAX_IMAGE_SIZE_MB}MB.`}>
          <div className="bg-[#fff9dc] border border-[#f5eabc] rounded-md p-6 flex flex-col items-center justify-center relative">
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="h-32 w-32 object-cover rounded-md mb-4"
                  referrerPolicy="no-referrer"
                />
              )}
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handleImageChange}
                className="hidden"
                id="beer-image-upload"
              />
              <label
                htmlFor="beer-image-upload"
                className="px-4 py-2 bg-[#d72134] text-white font-medium rounded-md hover:bg-[#b01a29] cursor-pointer flex items-center justify-center"
              >
                {imagePreview ? "Change Image" : "Upload Image"}
              </label>
              <p className="text-xs text-gray-500 mt-2">Max file size: {MAX_IMAGE_SIZE_MB}MB</p>
          </div>
        </Section>

        {/* 2. Beer Identity */}
        <Section number={2} title="Beer Identity" subtitle="Select the core style from the complete Beer Taxonomy.">
          {!isEditMode && !scopedRestaurantUuid && (
            <div>
              <label className={LABEL_CLASS}>Restaurant*</label>
              <select
                name="restaurant_uuid"
                value={formData.restaurant_uuid || ""}
                onChange={handleChange}
                className={inputClassName(validationErrors.restaurant_uuid)}
              >
                <option value="">Select restaurant</option>
                {restaurants.map((r) => (
                  <option key={r.uuid} value={r.uuid}>{r.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Brewery Name*</label>
              <input
                type="text"
                name="brewery_name"
                placeholder="Enter brewery name"
                value={formData.brewery_name || ""}
                onChange={handleChange}
                className={inputClassName(validationErrors.brewery_name)}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>Product Name*</label>
              <input
                type="text"
                name="product_name"
                placeholder="Enter beer or label name"
                value={formData.product_name || ""}
                onChange={handleChange}
                className={inputClassName(validationErrors.product_name)}
              />
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Beer Style* (searchable)</label>
            <Dropdown
              label="Select configured beer style"
              options={categories.map((cat) => ({ value: cat, label: cat }))}
              selectedOption={formData.style}
              onSelect={handleStyleSelect}
              searchable
              searchPlaceholder="Search beer styles..."
              className="w-full"
              buttonClassName={`w-full justify-between text-left ${validationErrors.style ? "border-red-500" : ""}`}
            />
            {validationErrors.style && <p className="text-xs text-red-600 mt-1">{validationErrors.style}</p>}
          </div>
        </Section>

        {/* 3. Style Profile */}
        <Section number={3} title="Style Profile" subtitle="Automatically derived from Beer Style. These values are read-only.">
          {styleProfile ? (
            <div className="space-y-3">
              <Badge className={getGroupBadgeColor(styleProfile.groupName)}>
                {styleProfile.groupName.replace(/\s*Beer$/, "")}
              </Badge>
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-gray-50 text-gray-700 border-gray-200">Body: {styleProfile.body}</Badge>
                <Badge className="bg-blue-50 text-blue-700 border-blue-200">Texture: {styleProfile.texture}</Badge>
                <Badge className="bg-purple-50 text-purple-700 border-purple-200">Flavor Intensity: {styleProfile.flavorIntensity}</Badge>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-left">Select a beer style to see its profile.</p>
          )}
        </Section>

        {/* 4. Origin */}
        <Section number={4} title="Origin" subtitle="Capture location from the largest geographical area to the smallest.">
          <div>
            <label className={LABEL_CLASS}>Country of Origin*</label>
            <input
              type="text"
              name="origin.country"
              placeholder="Enter country"
              value={formData.origin?.country || ""}
              onChange={handleChange}
              className={inputClassName(validationErrors.country)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>State / Province / Territory</label>
              <input
                type="text"
                name="origin.state"
                placeholder="Enter state or province"
                value={formData.origin?.state || ""}
                onChange={handleChange}
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>City / Town / Locality</label>
              <input
                type="text"
                name="origin.city"
                placeholder="Enter city"
                value={formData.origin?.city || ""}
                onChange={handleChange}
                className={INPUT_CLASS}
              />
            </div>
          </div>
          {originClassification && (
            <p className="text-xs text-gray-500 flex items-center gap-2 text-left">
              Origin classification:
              <Badge className={originClassification === "DOMESTIC" ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                {originClassification}
              </Badge>
              <span>Calculated automatically from restaurant and origin countries.</span>
            </p>
          )}
        </Section>

        {/* 5. Alcohol & Composition */}
        <Section number={5} title="Alcohol & Composition" subtitle="ABV and IBU remain numeric. Classification badges update automatically.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>ABV (%)*</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="25"
                name="abv"
                placeholder="0-25"
                value={formData.abv === undefined || formData.abv === null ? "" : formData.abv}
                onChange={handleChange}
                className={inputClassName(validationErrors.abv)}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>IBU</label>
              <input
                type="number"
                step="1"
                min="0"
                max="200"
                name="ibu"
                placeholder="0-200"
                value={formData.ibu || ""}
                onChange={handleChange}
                className={INPUT_CLASS}
              />
            </div>
          </div>
          {validationErrors.abv && <p className="text-xs text-red-600">{validationErrors.abv}</p>}
          {(abvCategory || ibuCategory) && (
            <div className="flex flex-wrap gap-2">
              {abvCategory && <Badge className={getLevelBadgeColor(abvCategory)}>ABV: {abvCategory}</Badge>}
              {ibuCategory && <Badge className={getLevelBadgeColor(ibuCategory)}>IBU: {ibuCategory}</Badge>}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
            {/* Non-Alcoholic is derived from ABV (< 0.5%, TTB threshold), not
                an independently editable attribute - a contradictory
                combination like 1.5% ABV + "Non-Alcoholic" checked was
                exactly the bug this replaces (Issue 29). */}
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <span className="text-gray-500">Non-Alcoholic:</span>
              <span className={`font-medium ${abvCategory === "Non-Alcoholic" ? "text-green-700" : abvCategory ? "text-gray-700" : "text-gray-400 italic"}`}>
                {abvCategory ? (abvCategory === "Non-Alcoholic" ? "Yes" : "No") : "Unknown — enter ABV"}
              </span>
            </div>
            <Checkbox name="is_vegan" label="Vegan" checked={formData.is_vegan} onChange={handleCheckboxChange} />
            <Checkbox name="has_gluten" label="Contains Gluten" checked={formData.has_gluten} onChange={handleCheckboxChange} />
            <Checkbox name="has_lactose" label="Contains Dairy / Lactose" checked={formData.has_lactose} onChange={handleCheckboxChange} />
            <Checkbox name="has_nuts" label="Contains Nuts" checked={formData.has_nuts} onChange={handleCheckboxChange} />
            <Checkbox name="is_filtered" label="Filtered" checked={formData.is_filtered} onChange={handleCheckboxChange} />
            <Checkbox name="seasonal" label="Seasonal" checked={formData.seasonal} onChange={handleCheckboxChange} />
            <Checkbox name="limited" label="Limited Release" checked={formData.limited} onChange={handleCheckboxChange} />
          </div>
        </Section>

        {/* 6. Serving Formats & Prices */}
        <Section number={6} title="Serving Formats & Prices" subtitle="Add structured serving rows instead of matching comma-separated lists.">
          <div className="space-y-3">
            {servingRows.map((row) => (
              <div key={row.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
                <div>
                  <label className={ROW_LABEL_CLASS}>Format</label>
                  <select
                    value={row.format}
                    onChange={(e) => handleServingRowChange(row.id, "format", e.target.value)}
                    className={INPUT_CLASS}
                  >
                    {FORMAT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={ROW_LABEL_CLASS}>Serving Size</label>
                  <input
                    type="text"
                    placeholder="e.g. 16 oz"
                    value={row.serving_size}
                    onChange={(e) => handleServingRowChange(row.id, "serving_size", e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={ROW_LABEL_CLASS}>Price</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={row.price}
                    onChange={(e) => handleServingRowChange(row.id, "price", e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveServingRow(row.id)}
                  className="px-3 py-2.5 text-sm font-medium text-red-600 border border-red-200 rounded hover:bg-red-50"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          {validationErrors.servingRows && <p className="text-xs text-red-600">{validationErrors.servingRows}</p>}
          <button
            type="button"
            onClick={handleAddServingRow}
            className="text-sm font-medium text-[#d72134] hover:text-[#b01a29]"
          >
            + Add serving option
          </button>
        </Section>

        {/* 7. Description & Administration */}
        <Section number={7} title="Description & Administration" subtitle="Customer-facing copy and internal operational details.">
          <div>
            <label className={LABEL_CLASS}>Beer Description</label>
            <textarea
              name="description"
              rows={3}
              placeholder="Enter menu-facing description..."
              value={formData.description || ""}
              onChange={handleChange}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>Training Notes</label>
            <textarea
              name="notes"
              rows={3}
              placeholder="Add internal service or rotation notes..."
              value={formData.notes || ""}
              onChange={handleChange}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="text-left">
              <p className="text-sm font-medium text-gray-800 text-left">Active</p>
              <p className="text-xs text-gray-500 text-left">Toggle off to mark this item inactive.</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={formData.status}
              onClick={() => setFormData((prev) => ({ ...prev, status: !prev.status }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.status ? "bg-[#d72134]" : "bg-gray-300"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.status ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        </Section>

        {/* Actions */}
        <div className="flex justify-end pt-4 border-t border-gray-200 gap-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-6 py-2 border border-gray-300 rounded font-medium text-gray-700 hover:bg-gray-50 focus:outline-none disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="px-6 py-2 bg-[#d72134] text-white rounded font-medium hover:bg-[#b01a29] focus:outline-none disabled:opacity-50 transition-colors"
          >
            {isSaving ? "Saving..." : item ? "Update Beer" : "Add Beer"}
          </button>
        </div>

      </form>
    </div>
  );
}
